import { useState, useMemo } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ReferenceArea,
} from "recharts";
import { getPairColor, PairAnalysisData, PairMetric, pairMetricConfig } from "@trader-dashboard/shared";
import { PairAnalysisTooltip } from "./PairAnalysisTooltip";

type PairAnalysisChartProps = {
  analysis: PairAnalysisData;
  metric: PairMetric;
};

const formatAxisDate = (dayKey: string): string => {
  const date = new Date(`${dayKey}T00:00:00`);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/**
 * Zooming works on index bounds into the point array rather than dates, so a
 * drag maps directly onto the rendered slice with no date parsing.
 */
export const PairAnalysisChart = ({ analysis, metric }: PairAnalysisChartProps): React.ReactNode => {
  const { points, pairs } = analysis;
  const [zoom, setZoom] = useState<{ start: number; end: number } | undefined>(undefined);
  const [dragStart, setDragStart] = useState<string | undefined>(undefined);
  const [dragEnd, setDragEnd] = useState<string | undefined>(undefined);

  const visiblePoints = useMemo(() => {
    if (!zoom) return points;
    return points.slice(zoom.start, zoom.end + 1);
  }, [points, zoom]);

  const applyZoom = () => {
    if (dragStart === undefined || dragEnd === undefined || dragStart === dragEnd) {
      setDragStart(undefined);
      setDragEnd(undefined);
      return;
    }

    const offset = zoom ? zoom.start : 0;
    const a = visiblePoints.findIndex((p) => p.date === dragStart);
    const b = visiblePoints.findIndex((p) => p.date === dragEnd);
    setDragStart(undefined);
    setDragEnd(undefined);
    if (a === -1 || b === -1) return;

    const start = offset + Math.min(a, b);
    const end = offset + Math.max(a, b);
    // Keep at least two points so the chart never collapses to a single column.
    if (end - start < 1) return;
    setZoom({ start, end });
  };

  if (points.length === 0 || pairs.length === 0) {
    return (
      <div className="flex h-[420px] items-center justify-center text-sm text-muted-foreground">
        No orders in this time frame
      </div>
    );
  }

  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Drag across the chart to zoom into a date range
        </p>
        {zoom && (
          <button
            type="button"
            onClick={() => setZoom(undefined)}
            className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted/50"
          >
            Reset zoom
          </button>
        )}
      </div>
      <ResponsiveContainer width="100%" height={420}>
        <LineChart
          data={visiblePoints}
          onMouseDown={(e: any) => e?.activeLabel && setDragStart(e.activeLabel)}
          onMouseMove={(e: any) => dragStart && e?.activeLabel && setDragEnd(e.activeLabel)}
          onMouseUp={applyZoom}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            tickFormatter={formatAxisDate}
            tick={{ fontSize: 11, fill: "#9ca3af" }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={24}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 11, fill: "#9ca3af" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<PairAnalysisTooltip metric={metric} />} />
          {pairs.map(({ symbol }) => (
            <Line
              key={symbol}
              type="monotone"
              dataKey={symbol}
              name={symbol}
              stroke={getPairColor(symbol)}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 3 }}
              isAnimationActive={false}
            />
          ))}
          {dragStart && dragEnd && (
            <ReferenceArea x1={dragStart} x2={dragEnd} strokeOpacity={0.3} fillOpacity={0.1} />
          )}
        </LineChart>
      </ResponsiveContainer>
      <p className="text-xs text-muted-foreground">
        {pairMetricConfig[metric].description}
      </p>
    </div>
  );
};
